import { OrderPicklist, PageProps } from '../../components/types'
import HeaderDetail from '../../components/HeaderDetail'
import { BasePicklistItemColumn, PicklistExtraColumn } from '../../constants/Column'
import Modal from '../../components/Modal'
import Comment from '../../components/Comment'
import { formatDateFromUnix } from '../../utilities/Format'

import OrderPicklistReducer, { 
  INITIAL_STATE, 
  PicklistAction
} from '../../states/PicklistReducer'
import { 
  pdRequestPicklist,
  postComment,
  getRemotePicklistById,
  pdDeprecatedPicklist,
  extraPicklistsByOrderId,
  updateStatusComment
} from '../../states/PicklistAction'

import DataGrid, { FormatterProps, EditorProps, RowsChangeData } from 'react-data-grid'
import { useReducer, useEffect, useCallback, useState } from 'react'
import { AiOutlineCheckCircle, AiFillMinusCircle } from 'react-icons/ai'
import { IoMdAddCircle } from 'react-icons/io'
import { DgtPdOrderPicklist, DgtPdOrderPicklistExtraReturn, OrderType } from '../../models'
import RemoveItemConfirmEditor from '../../components/RemoveItemConfirmEditor'
import { PicklistStatus } from '../../constants/EnumType'
import { getOrder, getOrderQueryForFgInspectionTypes } from '../../apis';

import { useHistory } from 'react-router-dom'

const PicklistApproveDetail = ({ title, id, username, readOnly } : PageProps) => {

  const [ extras, setExtras ] = useState<DgtPdOrderPicklistExtraReturn[]>([])
  const [ isDisabled, setIsDisabled ] = useState(false)

  const [ state, dispatch ] = useReducer(
    OrderPicklistReducer, INITIAL_STATE);
  
  const history = useHistory()

  useEffect(() => {
    const fetchPicklists = async (id: string) => {
      const { header, items, comments } = await getRemotePicklistById(id!)
      dispatch({ type: PicklistAction.Inited, payload: { header, items, comments } })
      
      if (header?.orderType === OrderType.CLAIM ) {
        const orderFgInspectionTypes = await getOrder(header?.pk, getOrderQueryForFgInspectionTypes)
        if (!orderFgInspectionTypes?.fgInspectionTypes || orderFgInspectionTypes?.fgInspectionTypes?.length === 0) {
          setIsDisabled(true)
        }
      } else {
        setIsDisabled(false)
      }
    }
    fetchPicklists(id!)
  }, [ id ])

  useEffect(() => {
    const fetchExtras =async (orderId: string) => {
      const extras = await extraPicklistsByOrderId(orderId)
      setExtras(extras)
    }
    fetchExtras(id!)
  }, [ id ])

  const onApproved = async () => {
    dispatch({ type: PicklistAction.PicklistUpdating })
    const header = state.header!
    const updated = await pdRequestPicklist(header, username!)
    dispatch({ type: PicklistAction.PicklistUpdated, payload: { header: updated } })
    history.goBack()
  }

  const openDialog = () => {
    dispatch({ type: PicklistAction.OpenDialog })
  }

  const closeDialog = () => {
    dispatch({ type: PicklistAction.CloseDialog })
  }

  const onPostComment = useCallback(async (comment: string) => {
    const commentResponse = await postComment(
      state.header?.pk!,
      comment, 
      username!
    )
    dispatch({ type: PicklistAction.PostComment, payload : { comments: [commentResponse] } })
  }, [ state.header?.pk, username ])

  const onActiveComment = useCallback(async (orderId: string, skCommentId: string, active: boolean, version: number) => {
		const updatedComment = await updateStatusComment(orderId, skCommentId, active, version)
    dispatch({ type: PicklistAction.UpdateComment, payload: { comments: [updatedComment] } })
  }, [])

  const onRowsChange = async (rows: OrderPicklist[],
    changedInfo: RowsChangeData<OrderPicklist>) => {
    const changedRowIdx = changedInfo.indexes[0]
    const changedRow = rows[changedRowIdx]
    const updated = await pdDeprecatedPicklist(changedRow.pk, changedRow.sk, changedRow._version || 1, changedRow.deprecated || false)
    dispatch({ type: PicklistAction.PicklistItemsUpdated, payload: { 
      items: rows.map(row => row.sk === updated.sk ? updated : row)
    }})
  }

  const { header, items, showConfirmDialog, comments, isSubmitting } = state

  const isWhIssued = header?.status === PicklistStatus.WH_APPROVED

  return <div className="page-container">
    <HeaderDetail title={`${title} Order : ${id}`} header={header} />
    <div className='flex items-end justify-between mt-1 mb-2'>
      <small>{items.length} รายการ</small>
      {readOnly ? null : (
        isWhIssued ? <p className='italic text-green-600 '>WH Approved ไปแล้ว ({formatDateFromUnix(header?.approvedDateTime)})</p> : 
        <div className='right-0 flex flex-col items-end gap-1'>
          <button className='w-32 gap-1 btn-success' onClick={openDialog} disabled={isDisabled}>
            <AiOutlineCheckCircle />กดเพื่ออนุมัติ
          </button>
          {isDisabled ? <small className='italic text-red-500'>ไม่สามารถอนุมัติใบเบิกได้ กรุณาแจ้ง QC Line เพื่อเลือก REPORT การตรวจก่อน</small> : null}
        </div>
      )
      }
    </div>
    <DataGrid
      className='mb-3'
      style={{ height: "100%", contain: "none" }}
      columns={[...BasePicklistItemColumn, {
        key: 'deprecated', name: 'ยกเลิก', width: 80, formatter: (props: FormatterProps<DgtPdOrderPicklist>) => {
          if (readOnly) return <></>
          return <div className='flex items-center justify-center h-full cursor-pointer '>
            {props.row.deprecated ? 
              <IoMdAddCircle size={22} className='text-green-700 ' /> : 
              <AiFillMinusCircle size={22} className='text-gray-500 '/>}
          </div>
        }, editor(props: EditorProps<DgtPdOrderPicklist>) {
          return <RemoveItemConfirmEditor props={props} />
        }, editorOptions: {
          editOnClick: true, commitOnOutsideClick: true
        }, editable: !readOnly
      }]}
      rows={items}
      onRowsChange={onRowsChange}
      rowClass={row => row.deprecated ? 'deprecated-item-row':''}
      rowHeight={(arg) => {
        const itm = arg.row as DgtPdOrderPicklist
        if (itm.barcodePkg) return 55
        return 35
      }}
    />
    {extras.length > 0 ? 
    <>
      <small>รายการเบิกเพิ่ม {extras.length} รายการ</small>
      <DataGrid
        className='mb-3 '
        style={{ height: "100%", contain: "none" }}
        columns={PicklistExtraColumn}
        rows={extras}
        defaultColumnOptions={{ resizable: true }}
        rowHeight={row => {
          if (row.type === 'GROUP') return 35
          const items = row.row.items ? JSON.parse(row.row.items) : []
          return (items.length === 0 ? 1 : items.length) * 50
        }}
      />
    </> : null}
    <Comment comments={comments.sort((a, b) => a.sk.localeCompare(b.sk))} onPost={onPostComment} onActive={onActiveComment} username={username}/>
    <Modal show={showConfirmDialog} cancelText='ยกเลิก'
      onHide={closeDialog}
      okText="อนุมัติ"
      okTheme='success'
      okAction={onApproved}
      title={`คุณกำลังอนุมัติ ใบเบิกของ ORDER ${id}`}
      loading={isSubmitting}>
      <h6>คุณยืนยันใช่หรือไม่ ?</h6>
    </Modal>
  </div>
}

export default PicklistApproveDetail